// P25 — JSON-LD builders. All URLs are absolute; dates are trimmed to YYYY-MM-DD.
import { getYouTubeId, isInstagram } from '../lib/video';

const SITE_NAME = 'ArtistScanner';
const INSTAGRAM = 'https://www.instagram.com/artistscanner.ai';

const origin = () =>
  (typeof window !== 'undefined' && window.location ? window.location.origin : 'https://artistscanner.ai');
const abs = (path) => (!path ? origin() : String(path).startsWith('http') ? path : origin() + path);
const logoUrl = () => `${origin()}/brand/logomark-color.png`;
const d10 = (v) => (v ? String(v).slice(0, 10) : undefined);

export function orgSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: origin(),
    logo: logoUrl(),
    sameAs: [INSTAGRAM],
  };
}

export function websiteSchema() {
  const o = origin();
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: o,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${o}/delhi-ncr/artists?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

export function breadcrumbSchema(items = []) {
  const list = (items || []).filter(Boolean);
  if (!list.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: list.map((it, i) => {
      const el = { '@type': 'ListItem', position: i + 1, name: it.name };
      if (it.url) el.item = abs(it.url);
      return el;
    }),
  };
}

export function faqSchema(faqs = []) {
  const valid = (faqs || []).filter((f) => f && f.q && f.a);
  if (!valid.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: valid.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
}

export function collectionPageSchema({ path, name, description, cards = [], faqs = [] }) {
  const items = (cards || [])
    .filter((c) => c && c.slug)
    .map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: abs(`/artist/${c.slug}`),
      name: c.stage_name || c.name || undefined,
    }));
  const out = [{
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name,
    url: abs(path),
    ...(description ? { description } : {}),
    mainEntity: { '@type': 'ItemList', itemListElement: items },
  }];
  const faq = faqSchema(faqs);
  if (faq) out.push(faq);
  return out;
}

export function articleSchema({ path, name, description, datePublished, dateModified }) {
  const o = origin();
  const out = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: name,
    ...(description ? { description } : {}),
    mainEntityOfPage: abs(path),
    author: { '@type': 'Organization', name: SITE_NAME, url: o },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: { '@type': 'ImageObject', url: logoUrl() },
    },
  };
  const published = d10(datePublished);
  const modified = d10(dateModified);
  if (published) out.datePublished = published;
  if (modified || published) out.dateModified = modified || published;
  return out;
}

function videoObjectSchema(a, name) {
  const links = (a.video_links || []).filter(Boolean);
  if (!links.length) return null;
  const url = links[0];
  const ig = isInstagram(url);
  const yt = !ig ? getYouTubeId(url) : null;
  const thumb = (a.video_thumbnails && a.video_thumbnails[0])
    || (yt ? `https://i.ytimg.com/vi/${yt}/hqdefault.jpg` : null);
  const embedUrl = yt ? `https://www.youtube.com/embed/${yt}` : url;
  const out = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: `${name} — live performance`,
    description: `Performance video of ${name} on ArtistScanner.`,
    embedUrl,
  };
  if (thumb) out.thumbnailUrl = thumb;
  return out;
}

// Person (or MusicGroup) + makesOffer/Service/PriceSpecification, optional
// VideoObject, and AggregateRating + Review[] ONLY when a visible review has text.
export function artistSchemas(a, reviews = []) {
  if (!a) return [];
  const name = a.stage_name || a.name;
  const category = a.category || a.artist_type || (a.categories && a.categories[0]) || 'Performer';
  const isGroup = a.is_group === true || /band|orchestra|group|troupe/i.test(String(category));
  const type = isGroup ? 'MusicGroup' : 'Person';

  const person = {
    '@context': 'https://schema.org',
    '@type': type,
    name,
    url: a.slug ? abs(`/artist/${a.slug}`) : origin(),
    jobTitle: category,
    homeLocation: { '@type': 'Place', name: a.city || a.base_city || 'Delhi NCR' },
  };
  if (a.profile_image) person.image = abs(a.profile_image);
  const genres = (a.genres || []).filter(Boolean);
  if (genres.length) person.knowsAbout = genres;
  const sameAs = [a.instagram_url, a.youtube_url, a.spotify_url].filter(Boolean);
  if (sameAs.length) person.sameAs = sameAs;

  const low = Number(a.price_min || a.minimum_price || 0);
  let high = Number(a.price_max || 0);
  if (high && high <= low) high = 0;
  const hasPrice = a.has_price !== false && low > 0;
  const service = {
    '@type': 'Service',
    serviceType: category,
    areaServed: 'Delhi NCR',
    provider: { '@type': type, name },
  };
  const offer = { '@type': 'Offer', itemOffered: service };
  if (hasPrice) {
    offer.priceSpecification = {
      '@type': 'PriceSpecification',
      priceCurrency: 'INR',
      minPrice: low,
      ...(high ? { maxPrice: high } : {}),
    };
  }
  person.makesOffer = offer;

  const textReviews = (reviews || []).filter(
    (r) => (r.comment || r.review) && String(r.comment || r.review).trim(),
  );
  const ratingVal = Number(a.rating || 0);
  const count = Number(a.total_reviews || 0);
  if (textReviews.length && ratingVal > 0 && count > 0) {
    person.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: ratingVal.toFixed(1),
      reviewCount: count,
    };
    person.review = textReviews.slice(0, 10).map((r) => {
      const rv = {
        '@type': 'Review',
        reviewRating: { '@type': 'Rating', ratingValue: Number(r.rating || 0), bestRating: 5 },
        author: { '@type': 'Person', name: r.reviewer_name || 'A host' },
        reviewBody: r.comment || r.review,
      };
      const when = r.enquiry_date || r.created_at;
      if (when) rv.datePublished = d10(when);
      return rv;
    });
  }

  const out = [person];
  const video = videoObjectSchema(a, name);
  if (video) out.push(video);
  return out;
}
